// @ts-nocheck
import { gql } from "@apollo/client";
import { SpogCompany } from "../types/SpogCompany";

export const COMPANY_VIEW_QUERY = gql`
  query company($hgId: Identifier!) {
    company(hgId: $hgId) {
      data {
        hgId
        name
        url
        urlId
        urlInstallId
        description
        ownershipType
        telephone
        telephoneCountryCode
        fiscalYearEndMonth
        stockExchange {
          name
          ticker
        }
        naics_2012 {
          name
          code
          parentId
        }
        address {
          lineOne
          postalCode
        }
        latitude
        longitude
        locationPrimary {
          city
          state {
            name
            country {
              name
              alpha2
            }
          }
        }
        isMultiNational
        industry {
          name
        }
        revenueRange {
          min
          max
          band
        }
        employeeRange {
          min
          max
          band
        }
        duns
        orgTree {
          root
          descendants
        }
        parent {
          hgId
          name
          url
        }
        scores
      }
      count
    }
  }
`;

export const COMPANY_ORG_TREE_QUERY = gql`
  query CompanyOrgTreeQuery($hgId: Identifier) {
    search(filters: { hgIdsIncludingCorporateRelatives: [$hgId] }, per: 10000) {
      data {
        hgId
        name
        industry {
          name
        }
        employeeRange {
          min
          max
          band
        }
        revenueRange {
          min
          max
          band
        }
        locationPrimary {
          city
          state {
            name
            country {
              name
              alpha2
              region {
                name
              }
            }
          }
        }
        isDp
        isGp
        isGhq
        url
      }
      count
    }
  }
`;

export const COMPANY_COUNTRY_SUMMARY_QUERY = gql`
  query companyCountrySummary($filters: CompanyFilter) {
    search(
      filters: $filters
      page: 0
      per: 100000 #Arbitrarily high number so all are selected
    ) {
      data {
        hgId
        locationPrimary {
          state {
            country {
              name
              alpha2
            }
          }
        }
        revenueRange {
          min
          max
        }
      }
      count
    }
  }
`;

export const SPOG_COMPANY_QUERY = gql`
  query companySearch(
    $query: String!
    $page: Int
    $per: Int
    $sort: [SearchCompaniesV2Sort]
    $filters: SearchCompaniesV2Filters
  ) {
    searchCompaniesV2(
      query: $query
      page: $page
      per: $per
      sort: $sort
      filters: $filters
    ) {
      count
      data {
        duns
        employeeRange {
          band
          max
          min
        }
        hgId
        itSpend
        locationPrimary {
          city
          state {
            country {
              name
            }
            name
          }
        }
        name
        revenueRange {
          band
          max
          min
        }
        url
      }
    }
  }
`;

export const COMPANY_SPEND_BY_COUNTRY_QUERY = gql`
  query companySpendByCountry(
    $hgIdsIncludingCorporateRelatives: [Identifier]
    $categoryParentIds: [Identifier]
    $per: Int
  ) {
    spend(
      hgIdsIncludingCorporateRelatives: $hgIdsIncludingCorporateRelatives
      categoryParentIds: $categoryParentIds
      per: $per
      sort: [{ field: "sum_relative_values", direction: DESC }]
    ) {
      count
      data {
        sumRelativeValues
        country {
          alpha2
          name
        }
      }
    }
  }
`;

export const COMPANY_SPEND_BY_CATEGORY_QUERY = gql`
  query companySpendByCategory(
    $categoryIds: [Identifier]
    $hgIds: [Identifier]
    $per: Int = 1000
  ) {
    spend(hgIds: $hgIds, categoryIds: $categoryIds, per: $per) {
      count
      data {
        hgId
        category {
          id
          name
          description
          nameTree
          parentId
        }
        absoluteValue
      }
    }
  }
`;

export const COMPANY_AI_SPEND_BY_CATEGORY_QUERY = gql`
  query companyAiSpendByCategory($hgIds: [Identifier], $per: Int = 1000) {
    spend(hgIds: $hgIds, per: $per, spendType: AI) {
      count
      data {
        hgId
        category {
          id
          name
          description
          nameTree
          parentId
        }
        absoluteValue
      }
    }
  }
`;

export const COMPANY_CONTRACTS_QUERY = gql`
  query contracts($hgId: Identifier, $per: Int!, $page: Int!) {
    contract(
      companyFilters: { hgIds: [$hgId] }
      per: $per
      page: $page
      sort: [{ field: "end_date", direction: DESC }]
    ) {
      data {
        id
        title
        summary
        announcementDate
        startDate
        endDate
        term
        primaryVendor {
          name
        }
        customerName
        signingCountry
        dealValue
        customerDrivers
        performanceCriteria
        pricingStructure
        contractStructure
        largerContract
        contractEvent
        bidProcess
        serviceLines {
          delivery
          geographicScope
          serviceTypes
        }
      }
      count
    }
  }
`;

export const COMPANY_PROFILE_ROW_COUNT_QUERY = gql`
  query companyProfileRowCountCheck(
    $hgId: Identifier!
    $intent: CompanyProfileIntentParams
    $install: CompanyProfileInstallParams
  ) {
    companyProfileRowCountCheck(
      hgId: $hgId
      intent: $intent
      install: $install
    ) {
      data {
        intentRowCount
        faiRowCount
        installsRowCount
      }
    }
  }
`;

export interface ServiceLine {
  id: string;
  serviceTypes: string[];
  delivery: string;
  geographicScope: string | null;
}

export interface Contract {
  id: string;
  title: string;
  summary: string;
  announcementDate: string;
  startDate: string;
  endDate: string;
  term: number;
  primaryVendor: {
    name: string;
  };
  customerName: string;
  relationshipStructure?: string;
  signingCountry: string;
  dealValue: number;
  customerDrivers: string;
  performanceCriteria: string;
  pricingStructure: string;
  contractStructure: string;
  largerContract: boolean;
  contractEvent: string;
  bidProcess: string;
  serviceLines: ServiceLine[];
}
export interface CompanySpend {
  hgId: string;
  country: {
    alpha2: string;
    name: string;
  };
  category: {
    id: string;
    name: string;
    description: string;
    nameTree: string;
    parentId: string;
  };
  absoluteValue: number;
  sumRelativeValues: number;
}

export interface CompanySpendByCountryResponse {
  spend: {
    count: number;
    data: CompanySpend[];
  };
}

export interface CompanySpendByCategoryResponse {
  spend: {
    count: number;
    data: CompanySpend[];
  };
}

interface RowCountRequestPayload {
  hgId: string;
  // TODO: Intent is not implemented in the platform yet, so we need to add it later
  intent: {};
  install: {
    filters: {
      countryAlpha2s?: string[];
      productAttributes?: ProductAttributeFilter;
      products?: InclusionFilter[];
      vendors?: VendorPenetrationFilter[];
      productCategories?: ProductCategoryFilter;
    };
    includeCorporateDescendants: boolean;
  };
}

export interface CompanyProfileRowCountCheckResponse {
  companyProfileRowCountCheck: {
    data: {
      intentRowCount: number;
      faiRowCount: number;
      installsRowCount: number;
    };
  };
}
